import { useTranslation } from 'react-i18next';
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
} from 'recharts';
import type { DomainScore } from '@/lib/formsScoring';
import { TYPE } from '@/lib/typography';

interface FormsScoreDisplayProps {
  domainScores: DomainScore[];
  previousDomainScores?: DomainScore[];
  totalScore?: number;
  title?: string;
}

export function FormsScoreDisplay({
  domainScores,
  previousDomainScores,
  totalScore,
  title,
}: FormsScoreDisplayProps) {
  const { t } = useTranslation();
  const chartTitle = title ?? t('forms.responses.health_profile', 'Sundhedsprofil');
  const latestLabel = t('forms.responses.latest', 'Seneste');
  const previousLabel = t('forms.responses.previous', 'Forrige');
  const previousByDomain = new Map(
    (previousDomainScores ?? []).map((row) => [row.domain, row.score]),
  );
  const hasPrevious = domainScores.some((row) => previousByDomain.has(row.domain));

  const chartData = domainScores.map((row) => ({
    domain: row.domain,
    latest: row.score,
    previous: previousByDomain.get(row.domain) ?? 0,
    fullMark: 100,
  }));

  return (
    <div className="sv-radar">
      <div className="sv-box">
        <p className="h4">{chartTitle}</p>
        <div className="sv-radar-chart">
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={chartData} cx="50%" cy="50%" outerRadius="70%">
              <PolarGrid stroke="var(--line)" />
              <PolarAngleAxis
                dataKey="domain"
                tick={{ fill: 'var(--muted)', fontSize: 11 }}
              />
              <PolarRadiusAxis
                angle={90}
                domain={[0, 100]}
                tick={{ fill: 'var(--placeholder)', fontSize: 10 }}
              />
              {hasPrevious ? (
                <Radar
                  name={previousLabel}
                  dataKey="previous"
                  stroke="var(--muted)"
                  fill="none"
                  strokeDasharray="5 4"
                  strokeWidth={1.75}
                />
              ) : null}
              <Radar
                name={latestLabel}
                dataKey="latest"
                stroke="var(--teal)"
                fill="var(--teal)"
                fillOpacity={0.2}
                strokeWidth={2}
              />
            </RadarChart>
          </ResponsiveContainer>
        </div>
        {hasPrevious ? (
          <ul className="sv-radar-leg">
            <li>
              <i className="latest" aria-hidden />
              {latestLabel}
            </li>
            <li>
              <i className="prev" aria-hidden />
              {previousLabel}
            </li>
          </ul>
        ) : null}
        {totalScore !== undefined ? (
          <div className="sv-radar-total">
            <p className={TYPE.small}>{t('forms.responses.overall_score', 'Samlet score')}</p>
            <p className="sv-radar-sum num">
              {totalScore.toFixed(1)}/100
            </p>
          </div>
        ) : null}
      </div>

      <div className="sv-box">
        <p className="h4">{t('forms.responses.domain_scores', 'Domænescores')}</p>
        <ul className="sv-dom">
          {domainScores.map((domain) => (
            <li key={domain.domain}>
              <span className="sw" style={{ backgroundColor: domain.color }} />
              <span className="nm">{domain.domain}</span>
              <span className="bar" aria-hidden>
                <i
                  style={{
                    width: `${Math.min(100, domain.score)}%`,
                    backgroundColor: domain.color,
                  }}
                />
              </span>
              <span className="v num">{domain.score.toFixed(1)}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
